import { useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { PortalCreateBillingPaymentLinkDocument } from '@/graphql/generated/graphql'
import { useBilling } from '@/features/billing/hooks/useBilling'
import { usePaymentGateways } from '../hooks/usePaymentGateways'
import { GatewayPicker } from './GatewayPicker'
import { PaymentLinkPanel, type PaymentLinkData } from './PaymentLinkPanel'

interface PayBillingDialogProps {
  /** Factura a pagar; null = diálogo cerrado. */
  billingId: number | null
  onClose: () => void
  /** La factura quedó PAID (refrescar la vista que abrió el diálogo). */
  onPaid?: () => void
}

interface LinkState {
  totalReal: number
  currencyName: string
  link: PaymentLinkData
}

/**
 * Pago de una factura pendiente por link (pagos-zeus-3, decisión C): elegir
 * pasarela → generar/reutilizar el link → misma pantalla link/QR del wizard.
 * Si la factura es de activación, el negocio se activa al confirmarse el pago.
 */
export function PayBillingDialog({ billingId, onClose, onPaid }: PayBillingDialogProps) {
  const { gateways, loading: gatewaysLoading } = usePaymentGateways()
  const { billing, loading: billingLoading } = useBilling(billingId ?? undefined)
  const [gatewayId, setGatewayId] = useState<number | null>(null)
  const [currencyId, setCurrencyId] = useState<number | null>(null)
  const [linkState, setLinkState] = useState<LinkState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [generate, { loading }] = useMutation(
    PortalCreateBillingPaymentLinkDocument,
  )

  const reset = () => {
    setGatewayId(null)
    setCurrencyId(null)
    setLinkState(null)
    setError(null)
  }

  const handleGenerate = async () => {
    if (billingId === null || gatewayId === null || currencyId === null) return
    setError(null)
    try {
      const { data } = await generate({
        variables: {
          input: { billingId, paymentGatewayId: gatewayId, currencyId },
        },
      })
      const result = data?.portalCreateBillingPaymentLink
      if (result) {
        setLinkState({
          totalReal: result.totalReal,
          currencyName: result.currencyName,
          link: result.link,
        })
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'No se pudo generar el link de pago. Intenta de nuevo.',
      )
    }
  }

  return (
    <Dialog
      open={billingId !== null}
      onOpenChange={(open) => {
        if (!open) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pagar factura</DialogTitle>
        </DialogHeader>

        {billingId !== null && !linkState && (
          <div className="space-y-4">
            <GatewayPicker
              gateways={gateways}
              loading={gatewaysLoading || billingLoading}
              selectedGatewayId={gatewayId}
              selectedCurrencyId={currencyId}
              fallbackCurrency={billing?.currency}
              onSelect={(gId, cId) => {
                setGatewayId(gId)
                setCurrencyId(cId)
              }}
              onSelectCurrency={setCurrencyId}
            />
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={gatewayId === null || currencyId === null || loading}
                onClick={() => void handleGenerate()}
              >
                {loading && <Loader2 className="size-4 animate-spin" />}
                Generar link de pago
              </Button>
            </div>
          </div>
        )}

        {billingId !== null && linkState && (
          <PaymentLinkPanel
            billingId={billingId}
            totalReal={linkState.totalReal}
            currencyName={linkState.currencyName}
            link={linkState.link}
            retrying={loading}
            onRetry={() => void handleGenerate()}
            onPaid={() => onPaid?.()}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
